import React, { useEffect, useRef, useState } from "react";
import { useContext } from "react";
import "./TicketChatPopup.css";
import attachmentSvg from "../../assets/images/attachment1.png";
import { SocketContext } from "../../SocketContext";

const TicketChatPopup = ({ ticket, onClose }) => {
    const { sendMessage, onSocketEvent, isConnected } = useContext(SocketContext);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [replyMessage, setReplyMessage] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setErrors] = useState("");
    const [success, setSuccess] = useState("");
    const [ticketStatus, setTicketStatus] = useState(ticket?.status || "");

    const chatMessagesRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const hasOpenedChatRef = useRef(false);

    // Keep the displayed status in sync when the selected ticket changes.
    useEffect(() => {
        setTicketStatus(ticket?.status || "");
    }, [ticket?._id, ticket?.status]);

    // Load ticket messages and listen for live Socket.IO events.
    useEffect(() => {
        if (!ticket?._id) return undefined;

        let active = true;
        const ticketId = ticket._id;
        const token = sessionStorage.getItem("token");
        const userId = sessionStorage.getItem("userId");

        setMessages([]);
        setLoadingMessages(true);
        setErrors("");
        shouldAutoScrollRef.current = true;
        hasOpenedChatRef.current = false;

        if (!token) {
            setErrors("Authentication token not found. Please log in again.");
            setLoadingMessages(false);
            return undefined;
        }

        const loadMessages = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BASE_URL}/api/v1/support/tickets/${ticketId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Unable to load messages (${response.status}).`
                    );
                }

                const data = await response.json();
                if (!active) return;

                // Merge HTTP results with any live messages received while loading.
                const fetchedMessages = Array.isArray(data.messages)
                    ? data.messages
                    : [];

                setMessages((previous) => {
                    const byId = new Map();

                    [...fetchedMessages, ...previous].forEach((message, index) => {
                        const key = message?._id || `temporary-${index}`;
                        byId.set(key, message);
                    });

                    return Array.from(byId.values()).sort((a, b) => {
                        const aTime = new Date(a.createdAt || 0).getTime();
                        const bTime = new Date(b.createdAt || 0).getTime();
                        return aTime - bTime;
                    });
                });

                if (data.status) {
                    setTicketStatus(data.status);
                }
            } catch (err) {
                if (active && err.name !== "AbortError") {
                    console.error("Fetch chat messages error:", err);
                    setErrors(err.message || "Failed to load chat messages.");
                }
            } finally {
                if (active) {
                    setLoadingMessages(false);
                }
            }
        };

        const joinTicket = () => {
            if (!active) return;

            sendMessage("ticketJoin", { userId, ticketId });

            // Re-fetch on initial connection/reconnection to recover missed messages.
            void loadMessages();
        };

        const handleTicketMessage = (data) => {
            if (
                !active ||
                data?.ticketId !== ticketId ||
                !data?.message
            ) {
                return;
            }

            const incomingMessage = data.message;

            setMessages((previous) => {
                if (
                    previous.some(
                        (message) => message._id === incomingMessage._id
                    )
                ) {
                    return previous;
                }

                return [...previous, incomingMessage].sort((a, b) => {
                    const aTime = new Date(a.createdAt || 0).getTime();
                    const bTime = new Date(b.createdAt || 0).getTime();
                    return aTime - bTime;
                });
            });

            if (
                incomingMessage.sender === "ADMIN" ||
                incomingMessage.sender === "SUPPORT"
            ) {
                sendMessage("ticketRead", { token, ticketId });
            }
        };

        const handleTicketStatus = (data) => {
            if (!active || data?.ticketId !== ticketId || !data?.status) {
                return;
            }

            setTicketStatus(data.status);
        };

        // SocketProvider owns the connection; subscribe through its exposed helpers.
        const unsubscribeConnect = onSocketEvent("connect", joinTicket);
        const unsubscribeMessage = onSocketEvent("ticketMessage", handleTicketMessage);
        const unsubscribeStatus = onSocketEvent("ticketStatus", handleTicketStatus);

        // If the provider exposes connection status, it can be used to join immediately.
        // The "connect" listener above handles initial connection and reconnects.
        // If already connected, NewSocket's onSocketEvent helper should invoke connect
        // handlers immediately or the provider can expose isConnected in context.
        void loadMessages();
        if (isConnected) {
            joinTicket();
        }

        return () => {
            active = false;
            sendMessage("ticketLeave", { ticketId });
            unsubscribeConnect?.();
            unsubscribeMessage?.();
            unsubscribeStatus?.();
        };
    }, [ticket?._id, sendMessage, onSocketEvent, isConnected]);

    // Scroll to the bottom on first open and when the user is already near the bottom.
    const handleChatScroll = () => {
        const container = chatMessagesRef.current;
        if (!container) return;

        const distanceFromBottom =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;

        shouldAutoScrollRef.current = distanceFromBottom < 100;
    };

    useEffect(() => {
        if (loadingMessages) return;

        const container = chatMessagesRef.current;
        if (!container) return;

        requestAnimationFrame(() => {
            const currentContainer = chatMessagesRef.current;
            if (!currentContainer) return;

            if (!hasOpenedChatRef.current) {
                currentContainer.scrollTop = currentContainer.scrollHeight;
                hasOpenedChatRef.current = true;
                shouldAutoScrollRef.current = true;
            } else if (shouldAutoScrollRef.current) {
                currentContainer.scrollTop = currentContainer.scrollHeight;
            }
        });
    }, [messages, loadingMessages]);

    const getAttachmentUrl = (url) => {
        if (!url) return "#";

        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }

        return `${process.env.REACT_APP_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
    };

    const formatDate = (date) => {
        if (!date) return "";

        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return "";

        return parsedDate.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const setError = (message) => {
        setErrors(message);
        setTimeout(() => setErrors(""), 4000);
    };

    const handleAttachmentChange = (event) => {
        const files = Array.from(event.target.files || []);

        const allowedFiles = files.filter(
            (file) =>
                file.type.startsWith("image/") ||
                file.type === "application/pdf"
        );

        const invalidFiles = files.filter(
            (file) =>
                !file.type.startsWith("image/") &&
                file.type !== "application/pdf"
        );

        if (invalidFiles.length > 0) {
            setError("Only PDF files and images are allowed.");
        } else {
            setErrors("");
        }

        if (allowedFiles.length > 0) {
            setAttachments((previous) => [...previous, ...allowedFiles]);
        }

        event.target.value = "";
    };

    const removeAttachment = (indexToRemove) => {
        setAttachments((previous) =>
            previous.filter((_, index) => index !== indexToRemove)
        );
    };


    const handleSendReply = async (event) => {
        event.preventDefault();

        if (!replyMessage.trim() && attachments.length === 0) {
            setError("Please enter a message or select an attachment.");
            return;
        }

        setSending(true);
        setErrors("");
        setSuccess("");

        try {
            const token = sessionStorage.getItem("token");

            if (!token) {
                throw new Error(
                    "Authentication token not found. Please log in again."
                );
            }

            const messageText = replyMessage.trim();
            const selectedAttachments = [...attachments];

            const formData = new FormData();
            formData.append("message", messageText);

            selectedAttachments.forEach((file) => {
                // Keep this field name consistent with your backend API.
                formData.append("", file, file.name);
            });

            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/v1/support/tickets/${ticket._id}/reply`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );

            let responseData = {};

            try {
                responseData = await response.json();
            } catch {
                // Response body may be empty.
            }

            if (!response.ok) {
                throw new Error(
                    responseData.message ||
                    `Failed to send reply (${response.status}).`
                );
            }

            // API directly returns the message object.
            const sentMessage = responseData;

            if (sentMessage?._id) {
                setMessages((previous) => {
                    const exists = previous.some(
                        (message) => message._id === sentMessage._id
                    );

                    if (exists) return previous;

                    return [...previous, sentMessage].sort(
                        (a, b) =>
                            new Date(a.createdAt).getTime() -
                            new Date(b.createdAt).getTime()
                    );
                });
            }

            setReplyMessage("");
            setAttachments([]);
            setSuccess("Reply sent successfully.");
            setTimeout(() => {
                setSuccess("");
            }, 2000);
        } catch (err) {
            console.error("Send reply error:", err);
            setError(err.message || "Failed to send reply.");
        } finally {
            setSending(false);
        }
    };


    if (!ticket) return null;

    return (
        <div
            className="ticket-chat-overlay"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="ticket-chat-popup"
                role="dialog"
                aria-modal="true"
                aria-label={`Chat for ticket ${ticket.ticketNo || ""}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="ticket-chat-header">
                    <div
                        className="ticket-chat-header-info"
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                        }}
                    >
                        <h3>Ticket #{ticket.ticketNo}</h3>
                        {ticketStatus && (
                            <span className="ticket-chat-status">
                                {ticketStatus.replaceAll("_", " ")}
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        className="ticket-chat-close"
                        onClick={onClose}
                        aria-label="Close chat"
                    >
                        &times;
                    </button>
                </div>

                <div
                    className="ticket-chat-messages"
                    ref={chatMessagesRef}
                    onScroll={handleChatScroll}
                >
                    {loadingMessages ? (
                        <div className="ticket-chat-info">Loading messages...</div>
                    ) : error && messages.length === 0 ? (
                        <div className="ticket-chat-error">{error}</div>
                    ) : messages.length === 0 ? (
                        <div className="ticket-chat-info">No messages found.</div>
                    ) : (
                        messages.map((message) => {
                            const isUser = message.sender === "USER";
                            const isRead = isUser
                                ? message.readByUser
                                : message.readByAdmin;

                            return (
                                <div
                                    key={message._id}
                                    className={`ticket-chat-message ${isUser
                                        ? "ticket-chat-user"
                                        : "ticket-chat-support"
                                        } ${Array.isArray(message.attachments) &&
                                            message.attachments.length > 0
                                            ? "attachments"
                                            : ""
                                        }`}
                                >
                                    {message.text && (
                                        <div className="ticket-chat-text">
                                            {message.text}
                                        </div>
                                    )}

                                    {Array.isArray(message.attachments) &&
                                        message.attachments.length > 0 && (
                                            <div className="ticket-chat-attachments">
                                                {message.attachments.map((file, index) => {
                                                    const fileUrl = getAttachmentUrl(file.url);
                                                    const isImage =
                                                        file.mimeType?.startsWith("image/");

                                                    return (
                                                        <div
                                                            key={
                                                                file._id ||
                                                                `${file.url}-${index}`
                                                            }
                                                            className="ticket-chat-attachment-item"
                                                        >
                                                            {isImage ? (
                                                                <a
                                                                    href={fileUrl}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="ticket-chat-image-link"
                                                                >
                                                                    <img
                                                                        src={fileUrl}
                                                                        alt={
                                                                            file.name ||
                                                                            "Chat attachment"
                                                                        }
                                                                        className="ticket-chat-attachment-image"
                                                                    />
                                                                </a>
                                                            ) : (
                                                                <a
                                                                    href={fileUrl}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="ticket-chat-attachment"
                                                                >
                                                                    {file.name ||
                                                                        "Open attachment"}
                                                                </a>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                    <div className="message-bottom">
                                        {isUser && (
                                            <span
                                                className={`ticket-chat-read-status ${isRead ? "is-read" : "is-unread"
                                                    }`}
                                            >
                                                {isRead ? "✓✓" : "✓"}
                                            </span>
                                        )}

                                        <div
                                            className="ticket-chat-date"
                                            style={{
                                                display: "flex",
                                                flexDirection: isUser
                                                    ? "row-reverse"
                                                    : "row",
                                            }}
                                        >
                                            {formatDate(message.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <form
                    className="ticket-chat-reply-form"
                    onSubmit={handleSendReply}
                >
                    {error && messages.length > 0 && (
                        <div className="ticket-chat-error">{error}</div>
                    )}

                    {success && (
                        <div className="ticket-chat-success">{success}</div>
                    )}

                    {attachments.length > 0 && (
                        <div className="ticket-chat-selected-files">
                            {attachments.map((file, index) => (
                                <div
                                    key={`${file.name}-${index}`}
                                    className="ticket-chat-selected-file"
                                >
                                    <span>{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachment(index)}
                                        aria-label={`Remove ${file.name}`}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="ticket-chat-reply-controls">
                        <label
                            htmlFor="ticket-chat-file"
                            className="ticket-chat-attach-button"
                            title="Attach files"
                        >
                            <img
                                src={attachmentSvg}
                                height={20}
                                width={20}
                                alt="attachment"
                            />
                        </label>

                        <input
                            id="ticket-chat-file"
                            type="file"
                            accept="image/*,.pdf,application/pdf"
                            multiple
                            onChange={handleAttachmentChange}
                            hidden
                        />

                        <textarea
                            className="ticket-chat-reply-input"
                            placeholder="Type your reply..."
                            value={replyMessage}
                            onChange={(event) =>
                                setReplyMessage(event.target.value)
                            }
                            rows={2}
                            disabled={sending}
                        />

                        <button
                            type="submit"
                            className="ticket-chat-send-button"
                            disabled={
                                sending ||
                                (!replyMessage.trim() && attachments.length === 0)
                            }
                        >
                            {sending ? "Sending..." : "Send"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TicketChatPopup;